import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { cleanSpecialCharacters } from "../../extras";

// SERVICES
import PaymentMethodsService from "../../api/paymentMethods";
// APP COMPONENTS
import RemoveModal from "../modals/removeModal";
import CollapseAlert from "../alerts/CollapseAlert";
import CreateOrEditPaymentMethodModal from "../paymentMethods/createOrEditPaymentMethodModal";
// MATERIAL UI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import ListItem from "@mui/material/ListItem";
import Paper from "@mui/material/Paper";

// ICONS
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PaymentIcon from "@mui/icons-material/Payment";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PixIcon from "@mui/icons-material/PixOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SavingsIcon from "@mui/icons-material/Savings";
import { dispatchSnackBarMessage } from "../../utils/snackbar";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: {
      duration: 0.2,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

// Get icon based on payment method name
const getPaymentMethodIcon = (methodName) => {
  const name = methodName.toLowerCase();

  if (name.includes("pix")) {
    return <PixIcon />;
  } else if (name.includes("dinheiro") || name.includes("cash")) {
    return <AttachMoneyIcon />;
  } else if (name.includes("débito") || name.includes("debit")) {
    return <CreditCardIcon />;
  } else if (name.includes("crédito") || name.includes("credit")) {
    return <CreditCardIcon />;
  } else if (name.includes("transfer") || name.includes("bank")) {
    return <AccountBalanceIcon />;
  } else if (name.includes("vale") || name.includes("voucher")) {
    return <SavingsIcon />;
  } else {
    return <PaymentIcon />;
  }
};

// Get color based on payment method name
const getPaymentMethodColor = (methodName, theme) => {
  const name = methodName.toLowerCase();

  if (name.includes("pix")) {
    return theme.palette.info.main;
  } else if (name.includes("dinheiro") || name.includes("cash")) {
    return theme.palette.success.main;
  } else if (name.includes("débito") || name.includes("debit")) {
    return theme.palette.warning.main;
  } else if (name.includes("crédito") || name.includes("credit")) {
    return theme.palette.secondary.main;
  } else if (name.includes("transfer") || name.includes("bank")) {
    return theme.palette.primary.main;
  } else if (name.includes("vale") || name.includes("voucher")) {
    return theme.palette.error.main;
  } else {
    return theme.palette.grey[700];
  }
};

const PaymentMethodItem = ({ paymentMethod, onEdit, onDelete }) => {
  const theme = useTheme();
  const methodName = cleanSpecialCharacters(paymentMethod.title);
  const icon = getPaymentMethodIcon(methodName);
  const color = getPaymentMethodColor(methodName, theme);

  return (
    <motion.div variants={itemVariants} exit="exit" layout>
      <Paper
        elevation={2}
        sx={{
          overflow: "hidden",
          mb: 2,
          borderRadius: 2,
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: theme.shadows[4],
            transform: "translateY(-2px)",
          },
          border: `1px solid ${alpha(color, 0.2)}`,
        }}
      >
        <ListItem
          disablePadding
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
          secondaryAction={
            <Stack direction="row" spacing={1}>
              <Tooltip title="Editar método de pagamento">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label="edit"
                    onClick={() => onEdit(paymentMethod)}
                    sx={{
                      color: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </motion.div>
              </Tooltip>
              <Tooltip title="Excluir método de pagamento">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label="delete"
                    onClick={() => onDelete(paymentMethod)}
                    sx={{
                      color: theme.palette.error.main,
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.error.main, 0.2),
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </motion.div>
              </Tooltip>
            </Stack>
          }
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.1),
                color: color,
                mr: 2,
              }}
            >
              {icon}
            </Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
              {methodName}
            </Typography>
            {paymentMethod.active && (
              <Chip
                icon={<CheckCircleIcon fontSize="small" />}
                label="Ativo"
                size="small"
                color="success"
                variant="outlined"
                sx={{ ml: 2 }}
              />
            )}
          </Box>
        </ListItem>
      </Paper>
    </motion.div>
  );
};

const PaymentMethodsList = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [editingPaymentMethod, setEditingPaymentMethods] = useState(null);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);

  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);
  const [showPaymentMethodDeleteModal, setShowPaymentMethodDeleteModal] =
    useState(false);

  const [openErrorAlert, setOpenErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setTimeout(async () => {
      await loadData();
    }, 10);
  }, []);

  const loadData = async () => {
    try {
      await loadPaymentMethods();
    } catch (e) {
      // Error is handled in loadPaymentMethods
    } finally {
      setLoading(false);
    }
  };

  const handleErrorTreatment = (error) => {
    const errorData = error.response?.data || {};
    let messages = "";
    for (const prop in errorData) {
      if (errorData.hasOwnProperty(prop)) {
        messages += errorData[prop].join("\n") + "\n";
      }
    }

    setErrorMessage(messages || "Ocorreu um erro. Tente novamente mais tarde.");
    setOpenErrorAlert(true);
  };

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      const res = await PaymentMethodsService.getPaymentMethods();
      setPaymentMethods(res.data);
    } catch (e) {
      handleErrorTreatment(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentMethodModal = () => {
    setShowPaymentMethodModal(!showPaymentMethodModal);
    setEditingPaymentMethods(null);
  };

  const createPaymentMethod = async (paymentMethodObj) => {
    try {
      await PaymentMethodsService.createPaymentMethod(paymentMethodObj);
      handlePaymentMethodModal();
      await loadPaymentMethods();
      dispatchSnackBarMessage(
        `create-payment-method-success-${new Date()}`,
        "Método de pagamento criado com sucesso!",
        "success"
      );
    } catch (e) {
      handleErrorTreatment(e);
    }
  };

  const handleEditPaymentMethod = (paymentMethod) => {
    setEditingPaymentMethods(paymentMethod);
    setShowPaymentMethodModal(!showPaymentMethodModal);
  };

  const editPaymentMethod = async (paymentMethodObj) => {
    try {
      await PaymentMethodsService.editPaymentMethod(
        paymentMethodObj,
        paymentMethodObj.id
      );

      handlePaymentMethodModal();
      setEditingPaymentMethods(null);
      await loadPaymentMethods();
      dispatchSnackBarMessage(
        `edit-payment-method-success-${new Date()}`,
        "Método de pagamento atualizado com sucesso!",
        "success"
      );
    } catch (e) {
      handleErrorTreatment(e);
    }
  };

  const handleDeletePaymentMethodModal = (paymentMethod) => {
    setPaymentMethodToDelete(paymentMethod);
    setShowPaymentMethodDeleteModal(!showPaymentMethodDeleteModal);
  };

  const handleDeletePaymentMethod = async () => {
    try {
      await PaymentMethodsService.deletePaymentMethod(paymentMethodToDelete.id);
      setPaymentMethodToDelete(null);
      setShowPaymentMethodDeleteModal(false);
      await loadPaymentMethods();
      dispatchSnackBarMessage(
        `delete-payment-method-success-${new Date()}`,
        "Método de pagamento removido com sucesso!",
        "success"
      );
    } catch (e) {
      handleErrorTreatment(e);
      setShowPaymentMethodDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Box sx={{ p: 3 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              sx={{ mb: 2, borderRadius: 2 }}
            />
            {[1, 2, 3, 4].map((item) => (
              <Skeleton
                key={item}
                variant="rectangular"
                width="100%"
                height={60}
                sx={{ mb: 2, borderRadius: 2 }}
              />
            ))}
          </Box>
        </motion.div>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <CollapseAlert
          errorMessage={errorMessage}
          openErrorAlert={openErrorAlert}
          setOpenErrorAlert={setOpenErrorAlert}
        />

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={handlePaymentMethodModal}
                color="primary"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: "medium",
                  boxShadow: theme.shadows[3],
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                }}
              >
                Novo Método de Pagamento
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        <Typography
          variant="h6"
          sx={{
            mb: 2,
            ml: 1,
            display: "inline-flex",
            alignItems: "center",
            "&::before": {
              content: '""',
              display: "inline-block",
              width: 4,
              height: 20,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 4,
              marginRight: 1.5,
            },
          }}
        >
          Seus Métodos de Pagamento Customizados
        </Typography>

        <Box
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            borderRadius: 3,
            p: 2,
            boxShadow: theme.shadows[1],
            backdropFilter: "blur(8px)",
          }}
        >
          {paymentMethods.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  px: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  borderStyle: "dashed",
                  borderWidth: 1,
                  borderColor: theme.palette.divider,
                }}
              >
                <PaymentIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.action.disabled,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Nenhum método de pagamento cadastrado
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  sx={{ mb: 2, maxWidth: 400 }}
                >
                  Cadastre os métodos de pagamento que você aceita nas suas
                  entregas.
                </Typography>
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={handlePaymentMethodModal}
                    sx={{ mt: 1, borderRadius: 2 }}
                  >
                    Adicionar Método de Pagamento
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <AnimatePresence>
                {paymentMethods.map((paymentMethod) => (
                  <PaymentMethodItem
                    key={paymentMethod.id}
                    paymentMethod={paymentMethod}
                    onEdit={handleEditPaymentMethod}
                    onDelete={handleDeletePaymentMethodModal}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </Box>
      </Box>

      <RemoveModal
        title="Excluir Método de Pagamento"
        message={`Tem certeza que deseja excluir o método de pagamento "${paymentMethodToDelete?.title}"?`}
        show={showPaymentMethodDeleteModal}
        toggleModal={handleDeletePaymentMethodModal}
        onRemove={handleDeletePaymentMethod}
      />

      {showPaymentMethodModal && (
        <CreateOrEditPaymentMethodModal
          show={showPaymentMethodModal}
          closeModal={handlePaymentMethodModal}
          createPaymentMethod={createPaymentMethod}
          editPaymentMethod={editPaymentMethod}
          paymentMethod={editingPaymentMethod}
        />
      )}
    </>
  );
};

export default PaymentMethodsList;
